import React, {useState, useMemo, useEffect} from "react";
import {
    Box, Container, Typography, Button, Card, CardContent,
    TextField, Chip, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, CircularProgress,
    InputAdornment, TablePagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import {Helmet} from "react-helmet";
import {useNavigate} from "react-router";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useBookOrders} from "../firebase/useBookOrders";
import {UnauthorizedAccess} from "../app/UnauthorizedAccess";
import {BookOrderDetailDialog} from "./BookOrderDetailDialog";
import {
    ORDER_STATUS,
    ORDER_STATUS_CONFIG,
    normalizeOrderStatus,
    calculateOrderKPIs,
    filterBookOrders,
    formatOrderDate,
    safeField,
} from "../book/bookOrderAdminUtils";

const STATUS_FILTERS = [
    {key: "ALL", label: "Alle"},
    {key: ORDER_STATUS.NEW, label: "Ny"},
    {key: ORDER_STATUS.SENT, label: "Sendt"},
    {key: ORDER_STATUS.INVOICED, label: "Fakturert"},
    {key: ORDER_STATUS.CANCELLED, label: "Kansellert"},
];

export const AdminBookOrders = () => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();
    const {orders, loading, updateOrderStatus} = useBookOrders();

    const [statusFilter, setStatusFilter] = useState("ALL");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        setPage(0);
    }, [statusFilter, searchQuery]);

    const kpis = useMemo(() => calculateOrderKPIs(orders), [orders]);
    const filteredOrders = useMemo(
        () => filterBookOrders(orders, {statusFilter, searchQuery}),
        [orders, statusFilter, searchQuery]
    );
    const paginatedOrders = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredOrders.slice(start, start + rowsPerPage);
    }, [filteredOrders, page, rowsPerPage]);

    if (!isAdmin) {
        return <UnauthorizedAccess requiredRole="administrator" />;
    }

    const handleSelectOrder = (order) => setSelectedOrder(order);

    return (
        <Box sx={{py: {xs: 4, sm: 6}, bgcolor: "#FBFBFA", minHeight: "85vh"}}>
            <Helmet>
                <title>Bokbestillinger | Admin</title>
            </Helmet>

            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/me")}
                    sx={{mb: 2.5, textTransform: "none", color: "primary.main"}}
                >
                    Tilbake til Min side
                </Button>

                <Typography variant="h4" component="h1" sx={{fontWeight: 800, color: "primary.main", mb: 3}}>
                    Bokbestillinger
                </Typography>

                {/* Summary KPIs */}
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: 2.5, mb: 4}}>
                    {[
                        {label: "Totalt antall bestillinger", value: kpis.totalOrders, color: "primary.main"},
                        {label: "Ubehandlede (nye)", value: kpis.newOrders, color: "warning.main"},
                        {label: "Totalt antall bøker", value: kpis.totalBooks, color: "primary.main"},
                    ].map((item) => (
                        <Card key={item.label} elevation={0} sx={{border: "1px solid rgba(40, 82, 56, 0.12)", borderRadius: 3}}>
                            <CardContent sx={{p: 2.5}}>
                                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                <Typography variant="h4" sx={{fontWeight: 800, color: item.color, mt: 0.5}}>{item.value}</Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Filters & Search */}
                <Box sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, gap: 2, mb: 3, alignItems: {md: "center"}, justifyContent: "space-between"}}>
                    <Stack direction="row" spacing={1} sx={{flexWrap: "wrap", gap: 1}}>
                        {STATUS_FILTERS.map((f) => (
                            <Chip
                                key={f.key}
                                label={f.label}
                                onClick={() => setStatusFilter(f.key)}
                                color={statusFilter === f.key ? "primary" : "default"}
                                variant={statusFilter === f.key ? "filled" : "outlined"}
                            />
                        ))}
                    </Stack>

                    <TextField
                        size="small"
                        placeholder="Søk i bestillinger (navn, e-post, sted, orgnr)..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{minWidth: {xs: "100%", md: 320}}}
                        inputProps={{"aria-label": "Søk i bestillinger"}}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>

                {/* Table or Empty State */}
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                        <CircularProgress />
                    </Box>
                ) : filteredOrders.length === 0 ? (
                    <Paper elevation={0} sx={{p: 4, textAlign: "center", border: "1px dashed rgba(40, 82, 56, 0.2)", borderRadius: 3}}>
                        <Typography variant="body1" color="text.secondary">
                            Ingen bestillinger funnet
                        </Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{border: "1px solid rgba(40, 82, 56, 0.12)", borderRadius: 3}}>
                        <Table aria-label="Bokbestillinger tabell">
                            <TableHead sx={{bgcolor: "rgba(40, 82, 56, 0.04)"}}>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 700}}>Dato</TableCell>
                                    <TableCell sx={{fontWeight: 700}}>Bestiller</TableCell>
                                    <TableCell sx={{fontWeight: 700}} align="center">Antall</TableCell>
                                    <TableCell sx={{fontWeight: 700}}>Leveringssted</TableCell>
                                    <TableCell sx={{fontWeight: 700}} align="center">Status</TableCell>
                                    <TableCell sx={{fontWeight: 700}} align="right">Handling</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedOrders.map((ord) => {
                                    const st = normalizeOrderStatus(ord.status);
                                    const cfg = ORDER_STATUS_CONFIG[st] || ORDER_STATUS_CONFIG[ORDER_STATUS.NEW];
                                    return (
                                        <TableRow key={ord.id} hover>
                                            <TableCell sx={{whiteSpace: "nowrap"}}>{formatOrderDate(ord.createdAt)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{fontWeight: 600}}>{safeField(ord.name)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{safeField(ord.email)}</Typography>
                                            </TableCell>
                                            <TableCell align="center">{safeField(ord.numberOfBooks, "0")}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{safeField(ord.delivery?.name)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{safeField(ord.delivery?.place)}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={cfg.label} color={cfg.color} size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    aria-label={`Åpne bestilling ${ord.id}`}
                                                    onClick={() => handleSelectOrder(ord)}
                                                >
                                                    Åpne
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filteredOrders.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[25, 50, 100]}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            labelRowsPerPage="Rader per side:"
                            labelDisplayedRows={({from, to, count}) => `${from}–${to} av ${count}`}
                            getItemAriaLabel={(type) => (type === "next" ? "Neste side" : "Forrige side")}
                        />
                    </TableContainer>
                )}

                <BookOrderDetailDialog
                    open={Boolean(selectedOrder)}
                    onClose={() => setSelectedOrder(null)}
                    order={selectedOrder}
                    onStatusChange={async (id, nextStatus) => {
                        await updateOrderStatus(id, nextStatus);
                        setSelectedOrder((prev) => (prev && prev.id === id ? {...prev, status: nextStatus} : prev));
                    }}
                />
            </Container>
        </Box>
    );
};
